import { LDEvaluationDetail, LDFlagValue } from '@afterpaytouch/feature'
import { getLast } from '@afterpay/utils/string'
import { AssetId } from './'

export const Experiments = {
  MerchantPreselectCheckout: 'merchant-preselect-checkout',
  EnableDobInfo: 'enable-dob-info',
  RegisterMarketingOpt: 'register-marketing-opt',
  ShowDonationMessage: 'enable-donation-experiment',
  ExchangeRateInDropdown: 'paylater.exchange-rate-in-dropdown-placement',
  CheckoutHideCardHolderName: 'checkout-hide-card-holder-name',
  CheckoutShowMerchantLogoInHeader: 'checkout-show-merchant-logo-in-header',
  CheckoutSummaryCashTooltip: 'checkout-summary-cash-tooltip-experiment',
  CheckoutShowPaymentScheduleOnAddPaymentMethod: 'checkout-show-payment-schedule-on-add-payment-method',
  PostCheckoutPersonalization: 'rocketship.show-personalization-in-post-checkout-experiment',
  CheckoutSocialProofExperiment: 'checkout-social-proof-experiment',
  CheckoutSocialProofExperimentUk: 'checkout-social-proof-experiment-uk',
  ProcessingRedirectionDelay: 'rocketship.processing-page-delay',
  CheckoutGiftCardFlowExperiment: 'checkout-gift-card-flow-experiment',
  AddFirstPaymentMethodExperiment: 'add-first-payment-method-experiment-us',
  PayIn2Experiment: 'paylater.checkout-pay-in-2-order-amount',
  SummaryPageImprovements: 'summary-page-improvements',
  NzCreditCheck: 'rocketship-nz-credit-check-enabled',
  SkipPrefillIfNoAccountExist: 'rocketship-skip-prefill-if-no-account-exist',
  PayMonthlySUP: 'paylater.pay-monthly-for-single-use-payments-enabled',
  InlineArcadeComponents: 'rocketship.inline-arcade-components',
  ConsumerLendingAutopayToggleExperiment: 'consumer-lending-autopay-toggle-experiment',
  CashCohortPreRedirectMessage: 'rocketship.cash-cohort-pre-redirect-message',
  SoftIdRetryEnabled: 'rocketship.soft-id-retry-enabled',
  ConsumerLendingEnableDynamicAprLabel: 'consumer-lending-enable-dynamic-apr-label',
} as const

export const HoldoutGroupExcludedExperiments = [
  'MerchantPreselectCheckout',
  'EnableDobInfo',
  'RegisterMarketingOpt',
  'ShowDonationMessage',
  'ExchangeRateInDropdown',
  'CheckoutHideCardHolderName',
  'CheckoutShowMerchantLogoInHeader',
  'CheckoutSummaryCashTooltip',
  'CheckoutShowPaymentScheduleOnAddPaymentMethod',
  'PostCheckoutPersonalization',
  'CheckoutSocialProofExperiment',
  'CheckoutGiftCardFlowExperiment',
  'VerifySmsDelay',
  'VerifyCallAuthz',
  'CheckoutSocialProofExperiment',
  'CheckoutSocialProofExperimentUk',
  'CreateAccountUseFormBuilder',
  'NzCreditCheck',
  'SkipPrefillIfNoAccountExist',
]

export type KnownExperiment = keyof typeof Experiments

export type ExperimentValue = 'A' | 'B' | 'C' | 'D' | 'E'
export interface ExperimentVariation {
  variation: ExperimentValue
  active: boolean
}
export interface Experiment {
  name: KnownExperiment
  active: boolean
  loading: boolean
  control: boolean
  value: ExperimentValue
  assetId?: AssetId
}

const inactiveReasons: readonly string[] = ['OFF', 'ERROR', 'PREREQUISITE_FAILED']

const loading = {
  loading: true,
  active: false,
  control: true,
  value: 'C',
} as const

const inactive = {
  loading: false,
  active: false,
  control: true,
  value: 'C',
} as const

const active = {
  loading: false,
  active: true,
}

const jsonEvaluation = (value?: LDFlagValue): ExperimentVariation => JSON.parse(value) as ExperimentVariation

const getExperimentValue = (value?: LDFlagValue): ExperimentVariation => {
  if (typeof value === 'boolean') {
    return value ? { variation: 'A', active: true } : { variation: 'C', active: true }
  }
  if (typeof value !== 'string') {
    return { variation: 'C', active: true }
  }
  try {
    return jsonEvaluation(value)
  } catch (err) {
    if (typeof value === 'undefined' || value === null) {
      return {
        variation: 'C',
        active: true,
      }
    }
    const last = getLast(value, 1)
    switch (last) {
      case 'A':
      case 'B':
      case 'C':
      case 'D':
      case 'E':
        return { variation: last, active: true }
      default:
        return { variation: 'C', active: false }
    }
  }
}

export const getExperimentAssetId = (value?: LDFlagValue): AssetId | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }

  const strings = value.split('__')
  const assetID = strings[0] as AssetId
  const validAssetIds = Object.values(AssetId)

  if (strings.length !== 2 || !validAssetIds.includes(assetID)) {
    return undefined
  }

  return assetID
}

export const getExperiment = (name: KnownExperiment, ldValue?: LDEvaluationDetail, defaultGroup?: ExperimentValue, useRuleMatch = false): Experiment => {
  if (typeof ldValue === 'undefined') {
    return { name, ...loading }
  }

  // If setting a special rule instead of the default rule, eg. country, LD will provide another variation list.
  // So should make the default rule as in pending state.
  // View an example in SocialBanner -> hooks.tsx
  if (useRuleMatch && ldValue.reason?.kind !== 'RULE_MATCH') {
    return { name, ...loading }
  }

  if (ldValue.reason?.errorKind === 'FLAG_NOT_FOUND') {
    // The 'FLAG_NOT_FOUND' can occur after the LD client has been closed. If this is the case, use the default group
    // from the active experiment cache, to avoid the experiment flipping values after the client has been closed.
    if (defaultGroup === undefined) {
      return { name, ...loading }
    } else {
      return getExperimentDetails(name, true, defaultGroup)
    }
  }

  if (inactiveReasons.includes(ldValue.reason?.kind ?? 'ERROR')) {
    return { name, ...inactive }
  }

  const experimentValue = getExperimentValue(ldValue.value)

  const usedGroup = experimentValue.active ? experimentValue.variation : 'C'
  const assetId = getExperimentAssetId(ldValue.value)

  return getExperimentDetails(name, experimentValue.active, usedGroup, assetId)
}

const getExperimentDetails = (name: KnownExperiment, active: boolean, value: ExperimentValue, assetId?: AssetId | undefined): Experiment => {
  return {
    name,
    active,
    loading: false,
    control: value === 'C',
    value,
    assetId,
  }
}

export const getEnableDOBInfoExperiment = (value?: string): Experiment => {
  const name = 'EnableDobInfo'
  if (typeof value === 'undefined') {
    return {
      name,
      ...loading,
    }
  }

  const experiment: Experiment = {
    name,
    ...inactive,
  }

  if (value === 'B' || value === 'C') {
    return {
      ...experiment,
      ...active,
      value,
      control: value === 'C',
    }
  }

  return experiment
}

export const getMarketingOptInExperiment = (value?: string): Experiment => {
  const name = 'RegisterMarketingOpt'
  if (typeof value === 'undefined') {
    return {
      name,
      ...loading,
    }
  }

  const experiment: Experiment = {
    name,
    ...inactive,
  }

  if (value === 'B' || value === 'C') {
    return {
      ...experiment,
      ...active,
      value,
      control: value === 'C',
    }
  }
  return experiment
}

export const getMerchantCheckoutPreselectExperiment = (checkoutButtonLabel: string | undefined): Experiment => {
  const name = 'MerchantPreselectCheckout'
  const experiment: Experiment = {
    name,
    ...inactive,
  }
  if (checkoutButtonLabel === 'Afterpay-preselected-on-merchant-site') {
    return {
      ...experiment,
      ...active,
      value: 'A',
      control: false,
    }
  } else if (checkoutButtonLabel === 'Afterpay-not-preselected-on-merchant-site') {
    return {
      ...experiment,
      ...active,
      value: 'C',
      control: true,
    }
  } else {
    return experiment
  }
}

export const getShowLogoInHeaderInfoExperiment = (value?: string): Experiment => {
  const name = 'CheckoutShowMerchantLogoInHeader'
  if (typeof value === 'undefined') {
    return {
      name,
      ...loading,
    }
  }

  const experiment: Experiment = {
    name,
    ...inactive,
  }

  if (value === 'B' || value === 'C') {
    return {
      ...experiment,
      ...active,
      value,
      control: value === 'C',
    }
  }
  return experiment
}

export type ExperimentVariant = 'ExperimentA' | 'ExperimentB' | 'ExperimentC' | 'Control'

const ExperimentVariantMap = {
  ExperimentA: 'A',
  ExperimentB: 'B',
  ExperimentC: 'D',
  Control: 'C',
}

export const getAddFirstPaymentMethodExperiment = (flagVariant: ExperimentValue, flowVariant: ExperimentVariant): Experiment => {
  const name = 'AddFirstPaymentMethodExperiment'

  if (typeof flagVariant === 'undefined') {
    return {
      name,
      ...loading,
    }
  }

  const experiment: Experiment = {
    name,
    ...inactive,
  }

  if (flagVariant === ExperimentVariantMap[flowVariant]) {
    return {
      ...experiment,
      ...active,
      value: flagVariant,
      control: flagVariant === 'C',
    }
  }

  return experiment
}

export const getPayIn2Experiment = (flagValue?: number): Experiment => {
  const name = 'PayIn2Experiment'
  if (typeof flagValue === 'undefined') {
    return {
      name,
      ...loading,
    }
  }
  const experiment: Experiment = {
    name,
    ...inactive,
  }
  if (flagValue >= -1) {
    return {
      ...experiment,
      ...active,
      value: flagValue > -1 ? 'A' : 'C',
      control: flagValue === -1,
    }
  }
  return experiment
}
